import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Spin, Button } from "antd";
import { Check, CaretDown, Plus, Buildings, DownloadSimple } from "@phosphor-icons/react";
import { useBranchContext } from "../../../contexts/branch-context";
import { BranchAvatar } from "./BranchAvatar";
import { colors, spacing, shadows } from "../../../styles/theme";
import { CreateBranchModal } from "../create-branch-modal";
import { ImportBranchModal } from "../import-branch-modal/ImportBranchModal";
import { useImpersonation } from "../../../contexts/impersonation-context/ImpersonationContext";

interface BranchSwitcherProps {
  collapsed?: boolean;
}

const SwitcherContainer = styled.div<{ collapsed?: boolean }>`
  position: relative;
  width: ${props => props.collapsed ? '48px' : '100%'};
  padding: 0;
`;

const SwitcherButton = styled(Button) <{ collapsed: boolean; isOpen: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${props => props.collapsed ? '0' : `${spacing.sm} ${spacing.md}`};
  background: ${props => props.isOpen ? colors.backgroundHover : 'transparent'};
  border: 1px solid ${props => props.isOpen ? colors.borderLight : 'transparent'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  min-height: 48px;
  height: 48px;

  &:hover {
    background: ${colors.backgroundHover};
    border-color: ${colors.borderLight};
  }

  ${props => props.collapsed && `
    justify-content: center;
    width: 48px;
    min-width: 48px;
    padding: 0;
  `}
`;

const BranchInfo = styled.div`
  flex: 1;
  text-align: left;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BranchName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
`;

const BranchLabel = styled.div`
  font-size: 11px;
  color: ${colors.textSecondary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const IconWrapper = styled.div<{ rotate?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.textSecondary};
  transition: transform 0.2s ease;
  transform: rotate(${props => props.rotate ? '180deg' : '0deg'});
  flex-shrink: 0;
`;

const Dropdown = styled.div<{ isOpen: boolean; collapsed?: boolean }>`
  position: ${props => props.collapsed ? 'fixed' : 'absolute'};
  top: ${props => props.collapsed ? 'auto' : 'calc(100% + 4px)'};
  left: ${props => props.collapsed ? 'auto' : '0'};
  right: ${props => props.collapsed ? 'auto' : '0'};
  min-width: ${props => props.collapsed ? '280px' : 'auto'};
  background: ${colors.white};
  border: 1px solid ${colors.borderLight};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  padding: ${spacing.xs};
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.isOpen ? '0' : '-4px'});
  transition: all 0.15s ease;
  max-height: 400px;
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.borderLight};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.textSecondary};
  }
`;

const DropdownSection = styled.div`
  padding: ${spacing.xs} 0;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: ${spacing.xs} ${spacing.md};
  margin-bottom: ${spacing.xs};
`;

const DropdownItem = styled(Button) <{ isActive?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.isActive ? colors.primaryLight : 'transparent'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.12s ease;
  text-align: left;

  &:hover {
    background: ${props => props.isActive ? colors.primaryLight : colors.backgroundHover};
  }

  & + & {
    margin-top: 2px;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
  margin-top: 2px;
`;

const CheckIcon = styled.div`
  color: ${colors.primary};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const Separator = styled.div`
  height: 1px;
  background: ${colors.borderLight};
  margin: ${spacing.xs} 0;
`;

const AddBranchButton = styled(DropdownItem)`
  color: ${colors.primary};
  font-weight: 600;

  &:hover {
    background: ${colors.primaryLight};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.lg};
`;

const EmptyState = styled.div`
  padding: ${spacing.xl};
  text-align: center;
  color: ${colors.textSecondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.borderLight};
  margin-bottom: ${spacing.md};
  display: flex;
  justify-content: center;
`;

const EmptyText = styled.div`
  font-size: 14px;
  margin-bottom: ${spacing.sm};
`;

export const BranchSwitcher: React.FC<BranchSwitcherProps> = ({ collapsed = false }) => {
  const { t } = useTranslation();
  const { currentBranch, availableBranches, switchBranch, isLoading } = useBranchContext();
  const { isDeveloper } = useImpersonation();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Pozycjonuj dropdown przy otwarciu (dla collapsed view)
  useEffect(() => {
    if (isOpen && collapsed && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [isOpen, collapsed]);

  // Zamknij dropdown przy kliknięciu poza nim
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleBranchSelect = (branchId: number) => {
    switchBranch(branchId);
    setIsOpen(false);
  };

  const handleAddBranch = () => {
    setIsOpen(false);
    setIsCreateModalOpen(true);
  };

  const handleImportBranch = () => {
    setIsOpen(false);
    setIsImportModalOpen(true);
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spin size="small" />
      </LoadingContainer>
    );
  }

  if (!currentBranch && availableBranches.length === 0) {
    return (
      <SwitcherContainer>
        <EmptyState>
          <EmptyIcon>
            <Buildings weight="light" />
          </EmptyIcon>
          <EmptyText>{t("branchSwitcher.noBranches")}</EmptyText>
          <AddBranchButton onClick={handleAddBranch}>
            <Plus size={20} weight="bold" />
            <ItemInfo>
              <ItemName>{t("branchSwitcher.addFirstBranch")}</ItemName>
            </ItemInfo>
          </AddBranchButton>
        </EmptyState>
        <CreateBranchModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      </SwitcherContainer>
    );
  }

  if (!currentBranch) {
    return null;
  }

  // Gdy sidebar collapsed, pokaż tylko avatar lub ikonę
  if (collapsed) {
    return (
      <SwitcherContainer collapsed={collapsed}>
        <SwitcherButton
          ref={buttonRef}
          collapsed={collapsed}
          isOpen={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          title={currentBranch.name}
        >
          <BranchAvatar branch={currentBranch} size={40} />
        </SwitcherButton>

        <Dropdown
          ref={dropdownRef}
          isOpen={isOpen}
          collapsed={collapsed}
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
        >
          <DropdownSection>
            <SectionLabel>{t("branchSwitcher.yourBranches")}</SectionLabel>
            {availableBranches.map((branch) => (
              <DropdownItem
                key={branch.id}
                isActive={branch.id === currentBranch.id}
                onClick={() => handleBranchSelect(branch.id)}
              >
                <BranchAvatar branch={branch} size={32} />
                <ItemInfo>
                  <ItemName>{branch.name}</ItemName>
                  {branch.formattedAddress && (
                    <ItemMeta>{branch.formattedAddress}</ItemMeta>
                  )}
                </ItemInfo>
                {branch.id === currentBranch.id && (
                  <CheckIcon>
                    <Check size={18} weight="bold" />
                  </CheckIcon>
                )}
              </DropdownItem>
            ))}
          </DropdownSection>

          <Separator />

          <DropdownSection>
            <AddBranchButton onClick={handleAddBranch}>
              <Plus size={20} weight="bold" />
              <ItemInfo>
                <ItemName>{t("branchSwitcher.addBranch")}</ItemName>
              </ItemInfo>
            </AddBranchButton>
            {isDeveloper && (
              <AddBranchButton onClick={handleImportBranch}>
                <DownloadSimple size={20} weight="bold" />
                <ItemInfo>
                  <ItemName>{t("branchSwitcher.importBranch")}</ItemName>
                </ItemInfo>
              </AddBranchButton>
            )}
          </DropdownSection>
        </Dropdown>

        <CreateBranchModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
        <ImportBranchModal
          open={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
        />
      </SwitcherContainer>
    );
  }

  // Pełny widok z nazwą brancha
  return (
    <SwitcherContainer collapsed={collapsed}>
      <SwitcherButton
        ref={buttonRef}
        collapsed={collapsed}
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <BranchAvatar branch={currentBranch} size={32} />
        <BranchInfo>
          <BranchLabel>{t("branchSwitcher.branchLabel")}</BranchLabel>
          <BranchName>{currentBranch.name}</BranchName>
        </BranchInfo>
        <IconWrapper rotate={isOpen}>
          <CaretDown size={16} weight="bold" />
        </IconWrapper>
      </SwitcherButton>

      <Dropdown ref={dropdownRef} isOpen={isOpen} collapsed={false}>
        <DropdownSection>
          <SectionLabel>{t("branchSwitcher.yourBranchesCount", { count: availableBranches.length })}</SectionLabel>
          {availableBranches.map((branch) => (
            <DropdownItem
              key={branch.id}
              isActive={branch.id === currentBranch.id}
              onClick={() => handleBranchSelect(branch.id)}
            >
              <BranchAvatar branch={branch} size={32} />
              <ItemInfo>
                <ItemName>{branch.name}</ItemName>
                {branch.formattedAddress && (
                  <ItemMeta>{branch.formattedAddress}</ItemMeta>
                )}
              </ItemInfo>
              {branch.id === currentBranch.id && (
                <CheckIcon>
                  <Check size={18} weight="bold" />
                </CheckIcon>
              )}
            </DropdownItem>
          ))}
        </DropdownSection>

        <Separator />

        <DropdownSection>
          <AddBranchButton onClick={handleAddBranch}>
            <Plus size={20} weight="bold" />
            <ItemInfo>
              <ItemName>{t("branchSwitcher.addBranch")}</ItemName>
            </ItemInfo>
          </AddBranchButton>
          {isDeveloper && (
            <AddBranchButton onClick={handleImportBranch}>
              <DownloadSimple size={20} weight="bold" />
              <ItemInfo>
                <ItemName>{"Importuj oddzia\u0142"}</ItemName>
              </ItemInfo>
            </AddBranchButton>
          )}
        </DropdownSection>
      </Dropdown>

      <CreateBranchModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
      <ImportBranchModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
      />
    </SwitcherContainer>
  );
};
